import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { bankIdPaymentReportUpdated } from "store/actions/bankIdPaymentsActions";
//#endregion
//#region types
import { BankIdPaymentType } from "models/enums/payment";
import { BankIdPaymentsListProps, PaymentListItem, SalaryPaymentListItem } from "models/propTypes/paymentPropTypes";
import { CurrencyCode } from "models/enums/currency";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import GenericBankIdPaymentList from "./genericPaymentList";
import SalaryBankIdPaymentList from "./salaryPaymentList";
import { Grid } from "@mui/material";
import { useIncomingInvoiceService } from "serviceHooks";
//#endregion
import { EnumHelper, LocalizationHelperInstance, PaymentHelper } from "utils/helpers";

const AllBankIdPaymentList: React.FC<BankIdPaymentsListProps> = (props) => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const dispatch = useDispatch();
  const { paymentSigningInformation, selectedPayments } = props;
  const [loadingRows, setLoadingRows] = React.useState<PaymentListItem[]>([]);

  React.useEffect(() => {
    const currentLoadingRows = loadingRows.filter((r) =>
      paymentSigningInformation?.incomingInvoices?.some((i) => i.id === r.id)
    );
    setLoadingRows(currentLoadingRows);
  }, [paymentSigningInformation]);

  const onPaymentsListUpdate = (rows: PaymentListItem[]) => {
    const selectedIncomingInvoiceIds = rows
      .filter((r) => r.type === BankIdPaymentType.IncomingInvoice)
      .map((r) => r.id);
    const selectedReceiptIds = rows.filter((r) => r.type === BankIdPaymentType.Receipt).map((r) => r.id);

    const selectedIncomingInvoices = paymentSigningInformation?.incomingInvoices?.filter((i) =>
      selectedIncomingInvoiceIds.some((s) => s === i.id)
    );
    const selectedReceipts = paymentSigningInformation?.receipts?.filter((i) =>
      selectedReceiptIds.some((s) => s === i.id)
    );
    const updatedPayments = {
      ...selectedPayments,
      incomingInvoices: selectedIncomingInvoices,
      receipts: selectedReceipts,
    };
    props.onPaymentSelectionsChange && props.onPaymentSelectionsChange(updatedPayments);
  };

  const onPayoutListUpdate = (rows: SalaryPaymentListItem[]) => {
    let updatedPayments = { ...selectedPayments };
    EnumHelper.getNumericItemValues(BankIdPaymentType).forEach((key) => {
      if (key === BankIdPaymentType.PersonSalaryEntry || key === BankIdPaymentType.ManualPayout) {
        const selectedPayouts = rows.filter((r) => r.type === key).map((r) => r.item);
        updatedPayments = PaymentHelper.updateBankIdPaymentsByType(key, updatedPayments, selectedPayouts);
      }
    });
    props.onPaymentSelectionsChange && props.onPaymentSelectionsChange(updatedPayments);
  };

  const onSalaryPayoutListUpdate = (rows: SalaryPaymentListItem[]) => {
    let updatedPayments = { ...selectedPayments };
    EnumHelper.getNumericItemValues(BankIdPaymentType).forEach((key) => {
      if (key === BankIdPaymentType.SalaryPayout || key === BankIdPaymentType.WintSalaryEntry) {
        const selectedPayouts = rows.filter((r) => r.type === key).map((r) => r.item);
        updatedPayments = PaymentHelper.updateBankIdPaymentsByType(key, updatedPayments, selectedPayouts);
      }
    });
    props.onPaymentSelectionsChange && props.onPaymentSelectionsChange(updatedPayments);
  };

  const onAlreadyPaidClick = (row: PaymentListItem) => {
    setLoadingRows([...loadingRows, row]);
    return incomingInvoiceService.markAsAlreadyPaid(row.id).then((res) => {
      props.reloadHandler && props.reloadHandler((result) => dispatch(bankIdPaymentReportUpdated(result)));
    });
  };

  const salaryPayouts = (
    paymentSigningInformation?.salaryPayoutFileRows?.map(
      (s) =>
        ({
          item: s,
          type: BankIdPaymentType.SalaryPayout,
          select: selectedPayments?.salaryPayoutFileRows?.some((sp) => sp.id === s.id),
        }) as SalaryPaymentListItem
    ) ?? []
  ).concat(
    paymentSigningInformation?.wintSalaryEntries?.map(
      (w) =>
        ({
          item: w,
          type: BankIdPaymentType.WintSalaryEntry,
          select: selectedPayments?.wintSalaryEntries?.some((sp) => sp.id === w.id),
        }) as SalaryPaymentListItem
    ) ?? []
  );

  const payouts = (
    paymentSigningInformation?.personSalaryEntries?.map(
      (p) =>
        ({
          item: p,
          type: BankIdPaymentType.PersonSalaryEntry,
          select: selectedPayments?.personSalaryEntries?.some((sp) => sp.id === p.id),
        }) as SalaryPaymentListItem
    ) ?? []
  ).concat(
    paymentSigningInformation?.manualPayouts?.map(
      (m) =>
        ({
          item: m,
          type: BankIdPaymentType.ManualPayout,
          select: selectedPayments?.manualPayouts?.some((sp) => sp.id === m.id),
        }) as SalaryPaymentListItem
    ) ?? []
  );

  const incomingInvoicesAndReceipts = (paymentSigningInformation?.incomingInvoices ?? [])
    .map(
      (i) =>
        ({
          id: i.id,
          amount: { type: CurrencyCode.SEK, value: i.totalAmount },
          profileName: i.supplierName,
          dueDate: i.payDate,
          number: i.invoiceNumber,
          date: i.invoiceDate,
          type: BankIdPaymentType.IncomingInvoice,
          select: selectedPayments?.incomingInvoices?.some((s) => s.id === i.id),
          loading: loadingRows.some((lr) => lr.id === i.id),
        }) as PaymentListItem
    )
    .concat(
      (paymentSigningInformation?.receipts ?? []).map(
        (r) =>
          ({
            id: r.id,
            amount: { type: CurrencyCode.SEK, value: r.amount },
            profileName: r.personName,
            profileId: r.personId,
            dueDate: r.payoutDate,
            number: r.serialNumber?.toString(),
            date: r.receiptDate,
            type: BankIdPaymentType.Receipt,
            select: selectedPayments?.receipts?.some((s) => s.id === r.id),
          }) as PaymentListItem
      )
    );

  return (
    <Grid container spacing={3}>
      {incomingInvoicesAndReceipts.length > 0 && (
        <GenericBankIdPaymentList
          moreActions={(row: PaymentListItem) =>
            row.type === BankIdPaymentType.IncomingInvoice
              ? [
                  {
                    icon: IconDefinitionConstants.WCheck,
                    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.AlreadyPaid),
                    onClickHandler: onAlreadyPaidClick.bind(null, row),
                  },
                ]
              : []
          }
          items={incomingInvoicesAndReceipts}
          onRowSelectionsChange={onPaymentsListUpdate}
        ></GenericBankIdPaymentList>
      )}

      {payouts.length > 0 && (
        <SalaryBankIdPaymentList
          moreActions={(row: any) => []}
          items={payouts}
          onRowSelectionsChange={onPayoutListUpdate}
        ></SalaryBankIdPaymentList>
      )}

      {salaryPayouts.length > 0 && (
        <SalaryBankIdPaymentList
          moreActions={(row: any) => []}
          items={salaryPayouts}
          onRowSelectionsChange={onSalaryPayoutListUpdate}
        ></SalaryBankIdPaymentList>
      )}
    </Grid>
  );
};

export default AllBankIdPaymentList;
